import { Textarea } from '@ibsys/kit';
import { useSortableWhenAllowed } from '@/shared/hooks';
import { GripVerticalIcon } from 'lucide-react';
import { cn } from '@/shared/lib';
import { FC, useCallback, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useGenerateTitle } from '@/entities/content';
import { hasPredicateIssues } from '@/entities/predicate';
import { PredicateInput } from '@/features/PredicateInput';
import { useHasPermission } from '@/entities/profile';
import { ConfirmationButton, EditableTitle } from '@/shared/ui';
import { RuleCompetencies } from './RuleCompetencies';
import { RuleKnowledgeSets } from './RuleKnowledgeSets';
import { type BlockCardFormValues } from './types';

type RuleRowProps = {
  index: number;
  sortableId: string;
  /** Порядковый номер элемента в списке сортировки */
  sortableIndex: number;
  onRemove: () => void;
  isUpdating: boolean;
};

export const RuleRow: FC<RuleRowProps> = ({ index, sortableId, sortableIndex, onRemove, isUpdating }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { control, getValues, setValue } = useFormContext<BlockCardFormValues>();
  const { mutate: generateTitle } = useGenerateTitle();
  const nameGeneratedRef = useRef(false);

  const name = `rules.${index}` as const;
  const ruleName = useWatch({ control, name: `${name}.name` });

  const handleTextBlur = useCallback(() => {
    if (nameGeneratedRef.current) return;

    const rule = getValues(`rules.${index}`);
    if (!rule) return;

    const trimmedText = rule.text.trim();
    const wordCount = trimmedText.split(/\s+/).filter((w) => w.length > 0).length;

    if (wordCount >= 5 && !rule.name.trim()) {
      nameGeneratedRef.current = true;
      generateTitle(
        { text: trimmedText, entityType: 'expertReportRule' },
        {
          onSuccess: (response) => {
            setValue(`rules.${index}.name`, response.title, { shouldDirty: true });
          },
          onError: () => {
            nameGeneratedRef.current = false;
          },
        },
      );
    }
  }, [index, getValues, setValue, generateTitle]);

  const handleNameSave = useCallback(
    (newName: string) => {
      setValue(`rules.${index}.name`, newName, { shouldDirty: true });
    },
    [index, setValue],
  );

  const { ref, handleRef, isDragging } = useSortableWhenAllowed({ id: sortableId, index: sortableIndex }, allowAdmin);

  return (
    <div
      ref={ref}
      className={cn(
        '@container/rule-row group/rule relative rounded-lg border border-border p-3 pl-5 space-y-3',
        isDragging && 'opacity-50',
      )}
    >
      {/* Перетаскивание — только при праве на редактирование */}
      {allowAdmin && (
        <div
          className="absolute left-0.5 top-3 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/rule:opacity-100"
          ref={(el) => handleRef(el)}
        >
          <GripVerticalIcon size={16} />
        </div>
      )}

      {/* Заголовок: название + удаление */}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <EditableTitle
            value={ruleName}
            label={t('expertReport:renameRule')}
            placeholder={t('expertReport:ruleName')}
            onSave={handleNameSave}
            // block обязателен: на inline-span truncate не обрезает — текст наезжал бы на кнопку удаления
            textClassName="block max-w-full truncate text-sm font-medium"
            disabled={!allowAdmin}
          />
        </div>
        {allowAdmin && (
          <ConfirmationButton
            onConfirm={onRemove}
            isLoadingConfirm={isUpdating}
            title={t('expertReport:removeRule')}
            description={
              <Trans
                i18nKey="expertReport:removeRuleDescription"
                values={{ ruleName }}
                components={{ strong: <strong /> }}
              />
            }
            trigger={{
              variant: 'ghost',
              color: 'destructive',
              size: 'sm',
              children: t('expertReport:deleteRule'),
            }}
          />
        )}
      </div>

      {/* Ограничение и Текст — вертикальный стек, Ограничение первым */}
      <div className="space-y-3">
        <PredicateInput
          control={control}
          name={`${name}.predicate`}
          type="textarea"
          label={t('expertReport:predicate')}
          classNames={{ input: 'field-sizing-content' }}
          disabled={!allowAdmin}
          rules={{
            validate: (value) => !hasPredicateIssues(value) || t('common:predicateHasIssues'),
          }}
        />

        <Textarea
          control={control}
          name={`${name}.text`}
          label={t('expertReport:ruleText')}
          onBlur={handleTextBlur}
          classNames={{ input: 'resize-none field-sizing-content' }}
          disabled={!allowAdmin}
        />
      </div>

      {/* Сеты знаний */}
      <RuleKnowledgeSets index={index} />

      {/* Компетенции */}
      <RuleCompetencies ruleId={getValues(`rules.${index}`)?.id} />
    </div>
  );
};
